// /opt/isp_erp/frontend/src/pages/ftth/NapForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ftthAPI } from '../../api/ftth.api';

const NapForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();       // Si existe → modo edición
  const esEdicion = !!id;

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(esEdicion);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null); // NAP original en modo edición

  const [form, setForm] = useState({
    codigo: '',
    nombre: '',
    direccion: '',
    latitud: '',
    longitud: '',
    capacidad_puertos: 8,
    zona: '',
    sector: '',
    ciudad: '',
    observaciones: '',
    estado: 'ACTIVO',
  });

  // Cargar NAP si es edición
  useEffect(() => {
    if (!esEdicion) return;
    let activo = true;
    (async () => {
      setLoadingData(true);
      try {
        const nap = await ftthAPI.getNapById(id);
        if (!activo) return;
        setInfo(nap);
        setForm({
          codigo: nap.codigo ?? '',
          nombre: nap.nombre ?? '',
          direccion: nap.direccion ?? '',
          latitud: nap.latitud ?? '',
          longitud: nap.longitud ?? '',
          capacidad_puertos: nap.capacidad_puertos ?? 8,
          zona: nap.zona ?? '',
          sector: nap.sector ?? '',
          ciudad: nap.ciudad ?? '',
          observaciones: nap.observaciones ?? '',
          estado: nap.estado ?? 'ACTIVO',
        });
      } catch (err) {
        setError(err.response?.data?.detail || 'Error al cargar NAP');
      } finally {
        if (activo) setLoadingData(false);
      }
    })();
    return () => { activo = false; };
  }, [id, esEdicion]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (esEdicion) {
        // En edición solo enviamos los campos editables
        await ftthAPI.updateNap(id, {
          nombre: form.nombre,
          direccion: form.direccion,
          latitud: parseFloat(form.latitud),
          longitud: parseFloat(form.longitud),
          observaciones: form.observaciones || null,
          estado: form.estado,
        });
      } else {
        await ftthAPI.createNap({
          ...form,
          latitud: parseFloat(form.latitud),
          longitud: parseFloat(form.longitud),
          capacidad_puertos: parseInt(form.capacidad_puertos),
        });
      }
      navigate('/ftth/naps/lista');
    } catch (err) {
      setError(err.response?.data?.detail || `Error al ${esEdicion ? 'actualizar' : 'crear'} NAP`);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        {esEdicion ? '✏️ Editar NAP' : '➕ Agregar Nuevo NAP'}
      </Typography>

      {esEdicion && info && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
          <Typography variant="body2" color="text.secondary">
            Editando <b>{info.codigo}</b> — creado el{' '}
            {new Date(info.creado_en).toLocaleString('es-ES')}
          </Typography>
          <Box mt={1} display="flex" gap={1} flexWrap="wrap">
            <Chip size="small" label={`${info.puertos_ocupados}/${info.capacidad_puertos} puertos ocupados`} />
            <Chip size="small" label={`Ocupación ${info.porcentaje_ocupacion}%`} color={info.porcentaje_ocupacion >= 80 ? 'warning' : 'default'} />
            <Chip size="small" label={info.estado} color="info" />
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código"
                name="codigo"
                value={form.codigo}
                onChange={handleChange}
                required
                disabled={esEdicion}   // No permitimos cambiar código en edición
                placeholder="Ej: NAP-001"
                helperText={esEdicion ? 'El código no se puede modificar' : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre"
                name="nombre"
                value={form.nombre}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección"
                name="direccion"
                value={form.direccion}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Latitud"
                name="latitud"
                value={form.latitud}
                onChange={handleChange}
                required
                inputProps={{ step: 0.000001 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Longitud"
                name="longitud"
                value={form.longitud}
                onChange={handleChange}
                required
                inputProps={{ step: 0.000001 }}
              />
            </Grid>

            {!esEdicion && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Capacidad de Puertos"
                  name="capacidad_puertos"
                  value={form.capacidad_puertos}
                  onChange={handleChange}
                  inputProps={{ min: 1, max: 64 }}
                  helperText="No se puede cambiar después de crear"
                />
              </Grid>
            )}

            {esEdicion && (
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Estado"
                  name="estado"
                  value={form.estado}
                  onChange={handleChange}
                  SelectProps={{ native: true }}
                >
                  <option value="ACTIVO">ACTIVO</option>
                  <option value="MANTENIMIENTO">MANTENIMIENTO</option>
                  <option value="INACTIVO">INACTIVO</option>
                  <option value="LLENO">LLENO</option>
                </TextField>
              </Grid>
            )}

            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Zona" name="zona" value={form.zona} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Sector" name="sector" value={form.sector} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Ciudad" name="ciudad" value={form.ciudad} onChange={handleChange} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Observaciones"
                name="observaciones"
                value={form.observaciones}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <Button variant="outlined" onClick={() => navigate('/ftth/naps/lista')}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : (esEdicion ? 'Guardar cambios' : 'Guardar NAP')}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default NapForm;
